import { useEffect, useState } from 'react';
import { Warning, ArrowsClockwise, LinkSimple } from '@phosphor-icons/react';
import { fmtDate } from '@/lib/utils';
import { ReTramiteDialog } from '@/components/bootstrap/ReTramiteDialog';
import {
  BuscarLoteInline, CandidatoRow, ModeloPicker,
  selectedDeBusqueda, selectedDeCandidato, type SelectedLote,
} from '@/components/bootstrap/piezas';
import { useVincular } from '@/hooks/useBootstrap';
import type { ApiError } from '@/api/client';
import type { BootstrapGrupo, LoteCandidato, LoteBusqueda, ReTramiteRequerido } from '@/types';

// ============================================================
// Tarjeta de UN CODIGO. Es el camino de los grupos cuyo lote todavia NO esta
// decidido: AMBIGUOS (2+ candidatos, el humano elige — 9.4) y SIN SUGERENCIA
// (sin candidato, se resuelve con el buscador manual).
//
// Los grupos con candidato UNICO ya NO pasan por aca: se agrupan por lote en
// `LoteCard`, porque un lote acumula 2-5 codigos y decidir de a uno hacia que
// el 2o en adelante reventara con 409 (UX_TramiteAD_Vigente deja un vigente por
// lote). Ver la cabecera de GetPorVincular en el backend.
// ============================================================
export function GrupoCard({ grupo }: { grupo: BootstrapGrupo }) {
  const tieneCandidatos = grupo.candidatos.length > 0;

  // Lote seleccionado (de candidato O del buscador). null = sin panel.
  const [selected, setSelected] = useState<SelectedLote | null>(null);
  // Buscador: abierto por defecto cuando NO hay candidatos (unico camino).
  const [buscarAbierto, setBuscarAbierto] = useState(!tieneCandidatos);
  // Dialogo de re-tramite (PR 2): se abre desde el boton del 409, no auto.
  const [reTramiteOpen, setReTramiteOpen] = useState(false);

  // Modelo a vincular (opcional, 9.3). Default = RESERVADO si existe (el modelo
  // que realmente se construye), sino TRAMITADO. Re-prefill al cambiar de lote.
  const [idModelo, setIdModelo] = useState<number | null>(null);
  useEffect(() => {
    setIdModelo(selected?.idModeloReservado ?? selected?.idModeloTramitado ?? null);
  }, [selected?.idLote]); // eslint-disable-line react-hooks/exhaustive-deps

  const vincular = useVincular();

  // 409 de re-tramite: ApiError con status 409 + body.tramiteVigente. El boton
  // "Resolver re-trámite" abre el dialogo. Otros errores (concurrencia,
  // validacion) caen en `otroError` con su .message.
  const err = vincular.error as ApiError | null;
  const reTramite: ReTramiteRequerido | null =
    vincular.isError && err?.status === 409 && !!(err.body as ReTramiteRequerido | undefined)?.tramiteVigente
      ? (err.body as ReTramiteRequerido)
      : null;
  const otroError = vincular.isError && !reTramite ? (err?.message ?? 'Error al vincular') : null;

  // Rango de fechas: una sola fecha si coinciden, "a → b" si difieren.
  const f1 = fmtDate(grupo.fechaPrimera);
  const f2 = fmtDate(grupo.fechaUltima);
  const rango = f1 && f2 ? (f1 === f2 ? f1 : `${f1} → ${f2}`) : (f1 || f2);

  // Seleccionar resetea el estado de la mutacion previa (errores de otro intento).
  function selectCandidato(c: LoteCandidato) {
    setSelected(prev => (prev?.idLote === c.idLote ? null : selectedDeCandidato(c)));
    vincular.reset();
  }
  function pickBusqueda(l: LoteBusqueda) {
    setSelected(selectedDeBusqueda(l));
    vincular.reset();
  }
  function onVincular() {
    if (!selected) return;
    vincular.mutate({ idsCorreo: grupo.idsCorreo, idLote: selected.idLote, idModelo });
  }

  return (
    <div className="flex flex-col gap-2.5 rounded-2xl border border-brand-gray-200 bg-white p-3.5 shadow-sm">
      {/* Cabecera: tipo de codigo + codigo + cantidad + rango de fechas */}
      <div className="flex items-center gap-2">
        <span className="rounded bg-brand-gray-500 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
          {grupo.tipoCodigo}
        </span>
        <span className="font-mono text-sm font-semibold text-brand-gray-500">{grupo.codigo}</span>
        <span className="rounded-full bg-brand-gray-100 px-2 py-0.5 text-[11px] font-medium text-brand-gray-400">
          {grupo.cantidadCorreos} {grupo.cantidadCorreos === 1 ? 'correo' : 'correos'}
        </span>
        {rango && <span className="ml-auto font-mono text-[11px] text-brand-gray-400">{rango}</span>}
      </div>

      {/* Banner posible re-tramite (9.6): un candidato ya tiene tramite vigente */}
      {grupo.posibleReTramite && (
        <div className="flex items-center gap-1.5 rounded-lg bg-brand-yellow/15 px-2.5 py-1.5 text-xs text-brand-gray-500">
          <ArrowsClockwise size={14} weight="bold" className="shrink-0 text-brand-yellow" />
          Posible re-trámite: un candidato ya tiene trámite vigente.
        </div>
      )}

      {/* Tipos de correo del grupo */}
      {grupo.tipos.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {grupo.tipos.map(t => (
            <span key={t} className="rounded-full bg-brand-gray-100 px-2 py-0.5 text-[10px] font-medium text-brand-gray-400">
              {t}
            </span>
          ))}
        </div>
      )}

      {/* Texto de lote crudo (lo que el parser extrajo del correo) */}
      <div className="text-xs text-brand-gray-400">
        <span className="font-medium text-brand-gray-500">Texto de lote: </span>
        {grupo.textosLoteRaw.length > 0
          ? <span className="font-mono">{grupo.textosLoteRaw.join(' · ')}</span>
          : <span className="italic">— sin texto —</span>}
      </div>

      {/* Notas de triage (ej. "tipo no clasificado, revisar") */}
      {grupo.notas.map((n, i) => (
        <div key={i} className="flex items-start gap-1.5 rounded-lg bg-brand-yellow/15 px-2.5 py-1.5 text-[11px] text-brand-gray-500">
          <Warning size={13} weight="bold" className="mt-0.5 shrink-0 text-brand-yellow" />
          {n}
        </div>
      ))}

      {/* Seleccion del lote: candidatos del motor + buscador manual */}
      <div className="flex flex-col gap-1.5">
        {tieneCandidatos && (
          <>
            <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-gray-400">Candidatos</span>
            {grupo.candidatos.map(c => (
              <CandidatoRow
                key={c.idLote}
                c={c}
                selected={selected?.idLote === c.idLote}
                onSelect={() => selectCandidato(c)}
              />
            ))}
          </>
        )}

        {/* Buscador manual (escape de 9.4). Con candidatos: toggle "¿Otro lote?".
            Sin candidatos: abierto, es el unico camino. */}
        {tieneCandidatos ? (
          <button
            type="button"
            onClick={() => setBuscarAbierto(o => !o)}
            className="self-start text-[11px] text-brand-gray-400 hover:text-brand-gray-500"
          >
            {buscarAbierto ? '− Ocultar buscador' : '+ ¿Otro lote? Buscar manualmente'}
          </button>
        ) : (
          <span className="text-[11px] italic text-brand-gray-400">Sin candidatos — buscá el lote manualmente:</span>
        )}
        {buscarAbierto && <BuscarLoteInline onPick={pickBusqueda} selectedIdLote={selected?.idLote ?? null} />}
      </div>

      {/* Panel de vinculacion: aparece cuando hay un lote seleccionado */}
      {selected && (
        <div className="flex flex-col gap-2 rounded-lg border border-brand-gray-200 bg-brand-bg p-2.5">
          {/* Pre-aviso: el lote ya tiene tramite vigente -> vincular dara 409. */}
          {selected.tieneTramiteVigente && (
            <div className="flex items-start gap-1.5 rounded-lg bg-brand-yellow/15 px-2.5 py-2 text-[11px] text-brand-gray-500">
              <ArrowsClockwise size={14} weight="bold" className="mt-0.5 shrink-0 text-brand-yellow" />
              Este lote ya tiene trámite vigente — vincularlo va a requerir resolver re-trámite.
            </div>
          )}

          <ModeloPicker lote={selected} idModelo={idModelo} onChange={setIdModelo} />

          <button
            type="button"
            onClick={onVincular}
            disabled={vincular.isPending || vincular.isSuccess}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-green-100 px-3 py-1.5 text-sm font-semibold text-black hover:bg-brand-green-200 disabled:opacity-50"
          >
            <LinkSimple size={15} weight="bold" />
            {vincular.isSuccess
              ? 'Vinculado ✓'
              : vincular.isPending
                ? 'Vinculando…'
                : `Vincular a ${selected.idBd ?? `lote ${selected.idLote}`}`}
          </button>

          {/* 409 re-tramite: boton que abre el dialogo (9.6). No auto-open. */}
          {reTramite && (
            <div className="flex flex-col gap-1.5 rounded-lg bg-brand-yellow/15 px-2.5 py-2 text-[11px] text-brand-gray-500">
              <div className="flex items-start gap-1.5">
                <ArrowsClockwise size={14} weight="bold" className="mt-0.5 shrink-0 text-brand-yellow" />
                <span>El lote ya tiene un trámite vigente. Resolvé el re-trámite para continuar.</span>
              </div>
              <button
                type="button"
                onClick={() => setReTramiteOpen(true)}
                className="self-start font-semibold text-brand-gray-500 underline hover:text-brand-gray-400"
              >
                Resolver re-trámite →
              </button>
            </div>
          )}

          {/* Otros errores (concurrencia, validacion): mensaje del backend */}
          {otroError && (
            <div className="flex items-start gap-1.5 rounded-lg bg-brand-red-100/15 px-2.5 py-2 text-[11px] text-brand-red-200">
              <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
              {otroError}
            </div>
          )}
        </div>
      )}

      {/* Dialogo de re-tramite (9.6). Overlay fixed -> su lugar en el DOM no
          importa. onClose limpia tambien el 409 del vincular (oculta el boton).
          Al exito el dialogo llama onClose y la invalidacion desmonta la card. */}
      {reTramiteOpen && reTramite && selected && (
        <ReTramiteDialog
          vinculando={{
            idsCorreo: grupo.idsCorreo,
            etiqueta: `${grupo.tipoCodigo} ${grupo.codigo}`,
            cantidadCorreos: grupo.cantidadCorreos,
            tipos: grupo.tipos,
            textosLoteRaw: grupo.textosLoteRaw,
          }}
          idLote={selected.idLote}
          idBd={selected.idBd}
          proyecto={selected.proyecto}
          idModeloInicial={idModelo}
          tramiteVigente={reTramite.tramiteVigente}
          onClose={() => { setReTramiteOpen(false); vincular.reset(); }}
        />
      )}
    </div>
  );
}
